import { toAbsoluteArticlePhotoUrl } from "../../../../services/articlesPhotoServices";

export const PAGE_SIZE = 12;

export const FALLBACK_ARTICLE_IMAGE = "/img/no-image.jpg";

export const DEFAULT_BOUTIQUE_UI = {
  search: "",
  category: "all",
  stock: "all",
  sort: "order",
  perPage: PAGE_SIZE,
};

export const BOUTIQUE_UI_STORAGE_KEY = "boutique_dashboard_ui_state";

export const collatorFR = new Intl.Collator("fr", { sensitivity: "base" });

export const strip = (value = "") =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

export const readStorageJson = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

export const writeStorageJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
};

export const toNumberSafe = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

export const formatEUR = (value) =>
  toNumberSafe(value).toLocaleString("fr-FR", {
    style: "currency",
    currency: "EUR",
  });

export const normalizeArticle = (article) => {
  const isStockable = Boolean(
    article?.is_stockable ?? article?.is_stockage ?? false
  );

  return {
    ...article,
    is_stockable: isStockable,
    is_stockage: isStockable,
    quantite_stock: toNumberSafe(article?.quantite_stock),
    prix: toNumberSafe(article?.prix),
    taille: article?.taille || "",
    categorie: article?.categorie || "",
    ordre_article: Number(article?.ordre_article ?? 999999),
    is_active: Boolean(article?.is_active),
  };
};

export const getArticleImage = (article) => {
  const raw = article?.photo_url || "";
  return raw ? toAbsoluteArticlePhotoUrl(raw) : FALLBACK_ARTICLE_IMAGE;
};

export const isOutOfStock = (article) => {
  return (
    Boolean(article?.is_stockable) &&
    toNumberSafe(article?.quantite_stock) <= 0
  );
};

export const buildArticleSearchText = (article) => {
  return [
    article?.nom,
    article?.description,
    article?.categorie,
    article?.taille,
    article?.prix,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
};

export const getCartUnits = (cart = []) => {
  return cart.reduce((sum, item) => sum + toNumberSafe(item.quantity), 0);
};

export const getCartTotal = (cart = []) => {
  return cart.reduce(
    (sum, item) => sum + toNumberSafe(item.prix) * toNumberSafe(item.quantity),
    0
  );
};

export const canAddMoreToCart = (article, currentQuantity = 0) => {
  if (!article?.is_stockable) return true;
  return currentQuantity + 1 <= toNumberSafe(article.quantite_stock);
};

export const getStockLabel = (article) => {
  if (!article?.is_stockable) return "Stock non limité";

  const stock = toNumberSafe(article.quantite_stock);

  if (stock <= 0) return "Stock épuisé";
  if (stock <= 3) return `Stock faible : ${stock}`;

  return `Stock : ${stock}`;
};

export const getStockBadgeClass = (article) => {
  if (!article?.is_stockable) {
    return "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700";
  }

  const stock = toNumberSafe(article.quantite_stock);

  if (stock <= 0) {
    return "bg-red-50 text-red-700 ring-red-200 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/20";
  }

  if (stock <= 3) {
    return "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20";
  }

  return "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20";
};